import { useEffect } from "react"
import { Link, createFileRoute, useNavigate } from "@tanstack/react-router"
import { ArrowLeft } from "lucide-react"
import { COACH_FALLBACK_LABEL, iddProduktTraining, type IddTraining } from "@nl/shared"

import { GeneralCoachChat } from "@/components/coach-chat-general"
import { formatMinuten } from "@/components/idd/idd-format"
import { IddKapitelLeiste, IddKapitelListe } from "@/components/idd/idd-fortschritt"
import { IddPruefungsAbschnitt } from "@/components/idd/idd-pruefungs-abschnitt"
import { LoadState } from "@/components/load-state"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { useIddTraining, useIddTrainingNeuLaden } from "@/lib/idd-queries"
import { useCoach } from "@/lib/use-coach"

function Kopf({ training }: { training: IddTraining }) {
  return (
    <div className="mb-2 flex flex-wrap items-center gap-2">
      <Button asChild variant="ghost" size="sm">
        <Link to="/idd">
          <ArrowLeft className="h-4 w-4" />
          IDD-Bereich
        </Link>
      </Button>
      <Badge variant="outline" className="font-normal">
        IDD · {training.titel} · {formatMinuten(training.minuten)}
      </Badge>
      {!training.anrechenbar && (
        <Badge variant="secondary" className="font-normal">
          Übung, {training.jahr} bereits angerechnet
        </Badge>
      )}
    </div>
  )
}

function Ergebnis({ training }: { training: IddTraining }) {
  if (training.ergebnis === "angerechnet" && training.nachweisId) {
    return (
      <p role="status" className="rounded-xl bg-primary/10 p-4 text-[14px]">
        Geschafft: {formatMinuten(training.minuten)} IDD-Zeit angerechnet.{" "}
        <Link
          to="/idd/nachweis/$id"
          params={{ id: training.nachweisId }}
          className="font-medium underline underline-offset-4"
        >
          Zum Zertifikat
        </Link>
      </p>
    )
  }
  if (training.ergebnis === "wiederholung") {
    return (
      <p role="status" className="rounded-xl bg-muted/60 p-4 text-[14px]">
        Bestanden, aber nicht erneut angerechnet: dieses Modul zählt {training.jahr} bereits.
      </p>
    )
  }
  if (training.status === "abgebrochen") {
    return (
      <p role="status" className="rounded-xl bg-muted/60 p-4 text-[14px]">
        Abgebrochen. Dieses Training wird nicht angerechnet.
      </p>
    )
  }
  return null
}

/**
 * The IDD product training is the product training's coach chat in IDD mode.
 * Only the chapter progress above the thread and the knowledge check after
 * the last chapter are added.
 */
function TrainingPage() {
  const { id } = Route.useParams()
  const navigate = useNavigate()
  const { data: training, isError, refetch } = useIddTraining(id)
  const neuLaden = useIddTrainingNeuLaden(id)
  const coach = useCoach()

  // Scenarios live in their Durchlauf; an old link lands there.
  useEffect(() => {
    if (training?.processRunId) {
      void navigate({
        to: "/durchlauf/$runId",
        params: { runId: training.processRunId },
        replace: true,
      })
    }
  }, [training?.processRunId, navigate])

  if (!training || training.processRunId) {
    return (
      <LoadState failed={isError} onRetry={() => void refetch()}>
        Das Training konnte nicht geladen werden.
      </LoadState>
    )
  }

  if (training.art === "gespraech") {
    return (
      <div className="space-y-4">
        <Kopf training={training} />
        <p className="text-[14px] text-muted-foreground">
          Dieses Gesprächstraining stammt aus einer früheren Version und lässt sich nicht fortsetzen.
          Starte das Szenario im IDD-Bereich neu.
        </p>
      </div>
    )
  }

  const produkt = iddProduktTraining(training.modulKey)
  const erstes = training.kapitel[0]

  if (training.status !== "laufend") {
    return (
      <div className="h-full min-h-0 space-y-5 overflow-y-auto pb-8">
        <Kopf training={training} />
        <Ergebnis training={training} />
        <div className="grid gap-6 lg:grid-cols-[minmax(0,1fr)_300px]">
          <div className="min-w-0">
            {training.status !== "abgebrochen" && <IddPruefungsAbschnitt training={training} />}
          </div>
          <IddKapitelListe kapitel={training.kapitel} />
        </div>
      </div>
    )
  }

  return (
    <div className="flex h-full min-h-0 flex-col">
      <Kopf training={training} />
      <div className="min-h-0 flex-1">
        <GeneralCoachChat
          threadId={training.threadId}
          mode="idd"
          produkt={produkt?.produkt}
          extraBody={() => ({ iddTrainingId: training.id })}
          // The server binds the thread to the training on the first turn;
          // refetching picks up its id so the next turn continues it.
          onThreadCreated={() => neuLaden()}
          onReset={() => neuLaden()}
          onFinish={() => neuLaden()}
          aboveThread={
            <IddKapitelLeiste kapitel={training.kapitel} aktuell={training.aktuellesKapitel} />
          }
          empty={{
            title: `${coach?.name ?? COACH_FALLBACK_LABEL} führt dich durch ${training.kapitel.length} Lernbausteine.`,
            hint: "Wir lernen im Dialog: ich erkläre, frage nach und vertiefe mit dir. Nach dem letzten Baustein folgt die Lernerfolgskontrolle, bestanden ab 70 Prozent.",
            prompts: erstes ? [`Lass uns mit „${erstes.titel}“ anfangen.`] : ["Leg los."],
          }}
          placeholder="Deine Antwort …"
        />
      </div>
    </div>
  )
}

export const Route = createFileRoute("/_app/idd/training/$id")({
  component: TrainingPage,
})
